import { useCallback, useMemo } from 'react'
import type { ButtonVariant } from 'react-bootstrap/types'
import type { File } from '@ztechapps/common.contract'
import type { FileUploaderMode, OnSetFileMetaFunction, OnUploadCompleteFunction } from '@ztechapps/webui.input'
import type { BusyButtonProps } from '@ztechapps/webui.button'
import {
  getOutboundAttachmentLimitReason,
  outboundAttachmentMaxCount,
  outboundAttachmentMaxTotalBytes,
} from '@ztechapps/chat.common'
import { useToast } from '@ztechapps/common.hook'
import { BusyButton } from '@ztechapps/webui.button'
import { FileUploader } from '@ztechapps/webui.input'

export type AttachFileButtonProps = {
  attachments: File[]
  onAttach: (files: File[]) => void
  disabledReason?: string
  disabled?: boolean
  mode?: FileUploaderMode
  /** Unset keeps today's default look; the inbox toolbar end slot and both modals pass "link". */
  variant?: ButtonVariant
}

export const AttachFileButton = ({
  attachments,
  onAttach,
  disabledReason,
  disabled,
  mode,
  variant,
}: AttachFileButtonProps) => {
  const { showError } = useToast()
  // Memoized on `variant` and `disabledReason` — a stable identity keeps FileUploader from
  // remounting its trigger every render (a new component type each render forces React to
  // unmount it).
  const Trigger = useMemo(
    () => (props: BusyButtonProps) => (
      <BusyButton
        {...props}
        variant={variant}
        disabledReason={disabledReason}
        icon="paperclip"
        title="Attach files..."
        aria-label="Attach files"
        text=""
        busyText=" "
      />
    ),
    [variant, disabledReason]
  )

  const onUploadComplete: OnUploadCompleteFunction = async (files: File[] = []) => {
    const reason = getOutboundAttachmentLimitReason([...attachments, ...files])
    if (reason) {
      showError({ message: reason })
      return
    }
    onAttach(files)
  }

  const onSetFileMeta: OnSetFileMetaFunction = useCallback(() => {
    return {
      description: `Attached from chat.`,
    }
  }, [])

  return (
    <FileUploader
      onSetFileMeta={onSetFileMeta}
      onUploadComplete={onUploadComplete}
      disabled={disabled}
      mode={mode}
      trigger={Trigger}
      restrictions={{ maxNumberOfFiles: outboundAttachmentMaxCount, maxTotalFileSize: outboundAttachmentMaxTotalBytes }}
    />
  )
}
